import React, { useState, useEffect, useMemo, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal, IMsalContext } from '@azure/msal-react';
import { 
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { getFlowTypeUnits } from '../../../../Services/Global';
import { ApiService } from '../../../../Services/ApiService';
import { IMultiSectionUnitConfiguration, IUnitConfiguration } from '../../../../Models/IUnitConfiguration';
import { IPHEPlateType } from '../../../../Models/IPHEPlateType';
import { MultiSectionTiles } from '../../../Atom/MultiSectionVisualization/MultiSectionTiles';
import { FilterToggle } from '../../../Atom/Filtering/FilterToggle';
import { UCManagementFilter } from '../UCManagementFilter';
import { newUnitConfigurationFilter } from '../../../../Utils/UnitConfigurationHelper';
import { IUnitConfigurationFilter } from '../../../../Models/IUnitConfigurationFilter';
import logger from '../../../../Services/LoggingService';
import { createFlexStyles } from '../../../../theme/layoutStyles';

// Define column interface for Material UI table
interface Column {
  id: string;
  label: string;
  minWidth: number;
  maxWidth: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: any, item?: IUnitConfiguration) => React.ReactNode;
  sortable?: boolean;
}

// OPTIMIZED: Memoized table row component to prevent unnecessary re-renders
const MemoizedTableRow = memo(({ 
  row, 
  columns, 
  isSelected, 
  onRowClick, 
  styles, 
  theme 
}: { 
  row: IUnitConfiguration;
  columns: Column[];
  isSelected: boolean;
  onRowClick: (row: IUnitConfiguration) => void;
  styles: any;
  theme: any;
}) => {
  const handleClick = () => onRowClick(row);
  
  return (
    <TableRow
      hover
      onClick={handleClick}
      selected={isSelected}
      sx={{
        ...styles.tableRow,
        ...(row.isDisabled ? styles.disabledRow : {}),
        ...(isSelected ? { backgroundColor: `${theme.palette.primary.light}20` } : {})
      }}
    >
      {columns.map((column) => (
        <TableCell 
          key={column.id} 
          align={column.align || 'left'}
          style={{
            width: column.minWidth,
            maxWidth: column.maxWidth,
          }}
          sx={column.id === 'pheTypes' ? styles.pheTypesCell : styles.tableCell}
        >
          {column.format 
            ? column.format(row[column.id as keyof IUnitConfiguration], row)
            : String(row[column.id as keyof IUnitConfiguration] || '')
          }
        </TableCell>
      ))}
    </TableRow>
  );
});
MemoizedTableRow.displayName = 'MemoizedTableRow';

/**
 * UnitConfigurationManagement component to manage unit configurations using Material UI
 */
export const UnitConfigurationManagement: React.FC = () => {
  const ctx: IMsalContext = useMsal();
  const theme = useTheme();
  const flexStyles = createFlexStyles(theme);
  const navigate = useNavigate();
  const apiService = useMemo(() => new ApiService(ctx), [ctx]);
  const sectionDetailsRef = useRef<HTMLDivElement>(null);

  // State management
  const [currentData, setCurrentData] = useState<IUnitConfiguration[]>([]);
  const [dataColumns, setDataColumns] = useState<Column[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showFilters, setShowFilters] = useState(false);
  const [unitConfigurationFilter, setUnitConfigurationFilter] = useState<IUnitConfigurationFilter>(newUnitConfigurationFilter());
  const [filteredData, setFilteredData] = useState<IUnitConfiguration[]>([]);
  const [isInitialApiCallComplete, setIsInitialApiCallComplete] = useState<boolean>(false);
  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [sections, setSections] = useState<IMultiSectionUnitConfiguration[]>([]);
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  
  // Sorting state
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('');

  // OPTIMIZED: Memoize styles to prevent recreation on every render
  const styles = useMemo(() => ({
    container: {
      padding: 3,
      marginTop: 2,
    },
    contentWrapper: {
      width: '100%',
      maxWidth: '98%',
      mx: 'auto',
      overflowX: 'visible', 
      overflowY: 'visible' 
    },
    tableContainer: {
      mt: 3,
      width: '100%',
      boxShadow: theme.shadows[2],
      borderRadius: 1
    },
    table: {
      tableLayout: 'fixed',
      width: '100%'
    },
    headerCell: {
      fontWeight: 'bold',
      backgroundColor: theme.palette.background.paper,
      padding: '12px 16px',
      position: 'sticky',
      top: 0,
      zIndex: 1
    },
    tableRow: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    disabledRow: {
      backgroundColor: theme.palette.action.disabledBackground,
      opacity: 0.7
    },
    actionButton: {
      mr: 1
    },
    loadingContainer: {
      ...flexStyles.flexCenter,
      justifyContent: 'center', // Override for explicit center
      height: 200,
      width: '100%'
    },
    noResultsMessage: {
      textAlign: 'center',
      mt: 3,
      mb: 3
    },
    actionButtonsContainer: {
      ...flexStyles.flexCenterBetween,
      width: '100%',
      mb: 2
    },
    tableCell: {
      padding: '8px 16px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    pheTypesCell: {
      padding: '8px 16px',
      whiteSpace: 'normal',
      overflow: 'hidden',
      lineHeight: 1.2,
      height: 'auto',
      maxHeight: '5rem', // Approximately 4 lines of text
    },
    filterContainer: {
      p: 3, 
      bgcolor: 'background.default', 
      borderRadius: 1, 
      border: '1px solid', 
      borderColor: 'divider',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    }
  }), [theme, flexStyles]);

  // Define table columns
  const getColumns = (): Column[] => {
    let columns: Column[] = [
      {
        id: 'actions',
        label: 'Actions',
        minWidth: 80,
        maxWidth: 80,
        align: 'center',
        sortable: false,
        format: (value: any, item?: IUnitConfiguration) => (
          <Box>
            <Tooltip title="Edit configuration">
              {item?.isDisabled ? (
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={true}
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              ) : (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('unitconfigurationmanagement/unitconfigurationeditor', { state: { unitConfiguration: item } });
                  }}
                >
                  <EditIcon />
                </IconButton>
              )}
            </Tooltip>
            
            {process.env.NODE_ENV === 'development' && (
              <Tooltip title="Delete configuration">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteUnitConfiguration(item!);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )
      },
      {
        id: 'industry',
        label: 'Industry',
        minWidth: 90,
        maxWidth: 120,
        format: (value: any) => (
          <Tooltip title={value || ''}>
            <Typography variant="body2" noWrap>
              {value || ''}
            </Typography>
          </Tooltip>
        )
      },
      {
        id: 'subIndustry',
        label: 'Sub Industry',
        minWidth: 100,
        maxWidth: 140,
        format: (value: any) => (
          <Tooltip title={value || ''}>
            <Typography variant="body2" noWrap>
              {value || ''}
            </Typography>
          </Tooltip>
        )
      },
      {
        id: 'application',
        label: 'Application',
        minWidth: 90,
        maxWidth: 120,
        format: (value: any) => (
          <Tooltip title={value || ''}>
            <Typography variant="body2" noWrap>
              {value || ''}
            </Typography>
          </Tooltip>
        )
      },
      {
        id: 'subApplication',
        label: 'Sub App.',
        minWidth: 80,
        maxWidth: 100,
        format: (value: any) => (
          <Tooltip title={value || ''}>
            <Typography variant="body2" noWrap>
              {value || ''}
            </Typography>
          </Tooltip>
        )
      },
      {
        id: 'processStage',
        label: 'Process Stage',
        minWidth: 100,
        maxWidth: 120,
        format: (value: any) => (
          <Tooltip title={value || ''}>
            <Typography variant="body2" noWrap>
              {value || ''}
            </Typography>
          </Tooltip>
        )
      },
      {
        id: 'productLine',
        label: 'Product Line',
        minWidth: 100,
        maxWidth: 120,
        format: (value: any) => (
          <Tooltip title={value || ''}>
            <Typography variant="body2" noWrap>
              {value || ''}
            </Typography>
          </Tooltip>
        )
      },
      {
        id: 'alfaLavalPosition',
        label: 'Position',
        minWidth: 100,
        maxWidth: 130,
        format: (value: any) => (
          <Tooltip title={value || ''}>
            <Typography variant="body2" noWrap>
              {value || ''}
            </Typography>
          </Tooltip>
        )
      },
      {
        id: 'flowType',
        label: 'Flow',
        minWidth: 60,
        maxWidth: 80,
        align: 'center',
        format: (value: any) => (
          <Tooltip title={value || ''}>
            <Typography variant="body2" noWrap>
              {getFlowTypeUnits(value)}
            </Typography>
          </Tooltip>
        )
      },
      {
        id: 'pheTypes',
        label: 'PHE types',
        minWidth: 140,
        maxWidth: 160,
        format: (value: any) => {
          // Safely handle the pheTypes array
          let pheTypeString = '';
          if (Array.isArray(value)) {
            pheTypeString = value.join(', ');
          } else if (value) {
            pheTypeString = String(value);
          }
          
          return (
            <Tooltip title={pheTypeString}>
              <Typography 
                variant="body2" 
                sx={{ 
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  fontSize: '0.8rem',
                  lineHeight: 1.2
                }}
              >
                {pheTypeString}
              </Typography>
            </Tooltip>
          );
        }
      },
      {
        id: 'isMultiSection',
        label: 'Multisection',
        minWidth: 90,
        maxWidth: 100,
        align: 'center',
        format: (value: any) => (
          <Box sx={{ ...flexStyles.flexCenter, justifyContent: 'center' }}>
            {value && <Checkbox checked disabled size="small" />}
          </Box>
        )
      },
      {
        id: 'isDisabled',
        label: 'Action',
        minWidth: 110,
        maxWidth: 130,
        align: 'center',
        format: (value: any, item?: IUnitConfiguration) => (
          <Button
            variant={value ? "outlined" : "contained"}
            color={value ? "primary" : "primary"}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              enableDisableUnitConfiguration(item!, !value);
            }}
          >
            {value ? "Enable" : "Disable"}
          </Button>
        )
      }
    ];

    return columns;
  };

  /**
   * Initialize PHE Type options from API (matching original functionality)
   */
  const setPHETypeComboBoxOptions = async () => {
    try {
      const response = await apiService.getAsync('settings/pheplatetypes');
      if (!response.ok) {
        logger.error('Failed to fetch PHE plate types', new Error(`HTTP ${response.status}`), null, {
          component: 'UnitConfigurationManagement',
          method: 'setPHETypeComboBoxOptions'
        });
        return;
      }
      
      const data = await response.json();
      const pheData: IPHEPlateType[] = data;
      
      // This functionality is now handled by UCManagementFilter component
      // which fetches PHE types independently for the filter
      logger.info('PHE types loaded successfully', { count: pheData.length }, {
        component: 'UnitConfigurationManagement',
        method: 'setPHETypeComboBoxOptions'
      });
    } catch (error) {
      logger.error('Failed to fetch PHE plate types', error as Error, null, {
        component: 'UnitConfigurationManagement',
        method: 'setPHETypeComboBoxOptions'
      });
    }
  };

  /**
   * Enable or disable a unit configuration
   */
  const enableDisableUnitConfiguration = async (config: IUnitConfiguration, isDisabled: boolean) => {
    try {
      setIsLoading(true);
      config.isDisabled = isDisabled;
      const response = await apiService.putAsync(config, `settings/unitconfigurations`);
      
      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Could not enable/disable unit configuration. ${error}`);
      }
      
      await getAllUnitConfigs();
    } catch (error) {
      logger.error('Failed to update unit configuration', error as Error, {
        configKey: getUnitConfigKey(config),
        isDisabled
      }, {
        component: 'UnitConfigurationManagement',
        method: 'enableDisableUnitConfiguration'
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete a unit configuration
   */
  const deleteUnitConfiguration = async (config: IUnitConfiguration) => {
    try {
      setIsLoading(true);
      const response = await apiService.delete('settings/unitconfigurations', config);
      
      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Could not delete unit configuration. ${error}`);
      }

      // Update current data (using OR logic to match original)
      setCurrentData(currentData?.filter(c =>
        c.industry !== config.industry ||
        c.subIndustry !== config.subIndustry ||
        c.application !== config.application ||
        c.subApplication !== config.subApplication ||
        c.processStage !== config.processStage ||
        c.productLine !== config.productLine ||
        c.alfaLavalPosition !== config.alfaLavalPosition
      ));
      
      // Update filtered data
      setFilteredData(prevFiltered => prevFiltered.filter(c =>
        c.industry !== config.industry ||
        c.subIndustry !== config.subIndustry ||
        c.application !== config.application ||
        c.subApplication !== config.subApplication ||
        c.processStage !== config.processStage ||
        c.productLine !== config.productLine ||
        c.alfaLavalPosition !== config.alfaLavalPosition
      ));
    } catch (error) {
      logger.error('Failed to delete unit configuration', error as Error, {
        configKey: getUnitConfigKey(config)
      }, {
        component: 'UnitConfigurationManagement',
        method: 'deleteUnitConfiguration'
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get all unit configurations
   */
  const getAllUnitConfigs = async () => {
    setIsLoading(true);
    let sessionData: IUnitConfiguration[];
    let filteredData: IUnitConfiguration[];

    try {
      if (!isInitialApiCallComplete) {
        const response = await apiService.getAsync('settings/unitconfigurations');
        
        if (!response.ok) {
          throw new Error(`Failed to fetch unit configurations: ${response.statusText}`);
        }
        
        sessionData = await response.json();
        setIsInitialApiCallComplete(true);
      } else {
        sessionData = currentData;
      }

      filteredData = filterUnitConfigurations(sessionData);

      setCurrentData(sessionData);
      setFilteredData(filteredData);
    } catch (error) {
      logger.error('Failed to fetch unit configurations', error as Error, null, {
        component: 'UnitConfigurationManagement',
        method: 'getAllUnitConfigs'
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Filter unit configurations based on the current filter
   */
  const filterUnitConfigurations = (sessionData: IUnitConfiguration[]) => {
    let filteredData = [...sessionData];

    if (unitConfigurationFilter.industry !== '') {
      filteredData = filteredData.filter(c => c.industry === unitConfigurationFilter.industry);
    }
    if (unitConfigurationFilter.subIndustry !== '') {
      filteredData = filteredData.filter(c => c.subIndustry === unitConfigurationFilter.subIndustry);
    }
    if (unitConfigurationFilter.application !== '') {
      filteredData = filteredData.filter(c => c.application === unitConfigurationFilter.application);
    }
    if (unitConfigurationFilter.subApplication !== '') {
      filteredData = filteredData.filter(c => c.subApplication === unitConfigurationFilter.subApplication);
    }
    if (unitConfigurationFilter.productLine !== '') {
      filteredData = filteredData.filter(c => c.productLine === unitConfigurationFilter.productLine);
    }
    if (unitConfigurationFilter.processStage !== '') {
      filteredData = filteredData.filter(c => c.processStage === unitConfigurationFilter.processStage);
    }
    if (unitConfigurationFilter.alfaLavalPosition !== '') {
      filteredData = filteredData.filter(c => c.alfaLavalPosition === unitConfigurationFilter.alfaLavalPosition);
    }
    if (unitConfigurationFilter.singleSectionUnits === false) {
      filteredData = filteredData.filter(c => c.isMultiSection === true);
    }
    if (unitConfigurationFilter.multiSectionUnits === false) {
      filteredData = filteredData.filter(c => c.isMultiSection === false);
    }
    if (unitConfigurationFilter.disabledUnits === true) {
      filteredData = filteredData.filter(c => c.isDisabled === true);
    }
    if (unitConfigurationFilter.pheTypes.length > 0) {
      filteredData = filteredData.filter(c => c.pheTypes?.some(pt => unitConfigurationFilter.pheTypes.includes(pt)));
    }

    return filteredData;
  };

  /**
   * Generate a unique key for a unit configuration
   */
  const getUnitConfigKey = (config: IUnitConfiguration): string => {
    return `${config.industry}-${config.subIndustry}-${config.application}-${config.subApplication}-${config.processStage}-${config.productLine}-${config.alfaLavalPosition}`;
  };

  /**
   * Get sections for a unit configuration
   */
  const getSectionsAsync = async (uc: IUnitConfiguration) => {
    if (uc.isMultiSection) {
      try {
        setIsLoading(true);
        logger.debug('Fetching sections for unit configuration', {
          configKey: getUnitConfigKey(uc)
        }, {
          component: 'UnitConfigurationManagement',
          method: 'getSectionsAsync'
        });
        
        // Create a deep copy of the configuration object to avoid reference issues
        const configToSend = {
          industry: uc.industry,
          subIndustry: uc.subIndustry,
          application: uc.application,
          subApplication: uc.subApplication,
          processStage: uc.processStage,
          productLine: uc.productLine,
          alfaLavalPosition: uc.alfaLavalPosition,
          hotMediaType: uc.hotMediaType || '',
          coldMediaType: uc.coldMediaType || '',
          breakdownOfProcessStage: uc.breakdownOfProcessStage || '',
          inputs: uc.inputs || [],
          nonUsedProperties: uc.nonUsedProperties || [],
          flowType: uc.flowType || '',
          outputs: uc.outputs || [],
          prefferedUnit: uc.prefferedUnit || '',
          samplingId: uc.samplingId || ''
        };
        
        const response = await apiService.getAsync("settings/unitconfigurations/sections", configToSend);
        if (!response.ok) {
          throw new Error(`Failed to fetch sections: ${response.statusText}`);
        }

        const data: IMultiSectionUnitConfiguration[] = await response.json();
        logger.debug('Sections data received successfully', {
          sectionsCount: data.length,
          configKey: getUnitConfigKey(uc)
        }, {
          component: 'UnitConfigurationManagement',
          method: 'getSectionsAsync'
        });
        setSections(data);
      } catch (error) {
        logger.error('Failed to fetch sections', error as Error, {
          configKey: getUnitConfigKey(uc)
        }, {
          component: 'UnitConfigurationManagement',
          method: 'getSectionsAsync'
        });
        setSections([]);
      } finally {
        setIsLoading(false);
      }
    } else {
      setSections([]);
    }
  };

  /**
   * Toggle showing filters
   */
  const toggleShowFilters = () => {
    setShowFilters(!showFilters);
  };

  /**
   * Reset all filters to default values
   */
  const resetFilters = () => {
    const defaultFilter = newUnitConfigurationFilter();
    setUnitConfigurationFilter(defaultFilter);
    setIsFiltering(false);
  };

  /**
   * Handle filter change
   */
  const handleFilterChange = (filter: IUnitConfigurationFilter, isFiltering: boolean) => {
    setUnitConfigurationFilter({ ...filter });
    setIsFiltering(isFiltering);
  };

  /**
   * Handle row click to show sections
   */
  const handleRowClick = (row: IUnitConfiguration) => {
    const rowKey = getUnitConfigKey(row);
    logger.userAction('row clicked', {
      configKey: rowKey,
      isMultiSection: row.isMultiSection
    });
    
    // Only update if clicking a different row or toggling the same row
    if (selectedRowKey === rowKey) {
      // If clicking the same row, toggle it off
      logger.debug('Deselecting current row', { configKey: rowKey }, {
        component: 'UnitConfigurationManagement',
        method: 'handleRowClick'
      });
      setSelectedRowKey(null);
      setSections([]);
    } else {
      // If clicking a different row, select it and fetch sections
      logger.debug('Selecting new row', { configKey: rowKey }, {
        component: 'UnitConfigurationManagement',
        method: 'handleRowClick'
      });
      setSelectedRowKey(rowKey);
      getSectionsAsync(row);
    }
  };

  // Load data on component mount
  useEffect(() => {
    setDataColumns(getColumns());
    getAllUnitConfigs();
    setPHETypeComboBoxOptions(); // Initialize PHE types like in original
  }, []);

  // Filter data when filter changes
  useEffect(() => {
    if (isInitialApiCallComplete) {
      const filtered = filterUnitConfigurations(currentData);
      setFilteredData(filtered);
    }
  }, [unitConfigurationFilter, isInitialApiCallComplete]);

  // Scroll to section details when they become available
  useEffect(() => {
    if (sections && sections.length > 0 && sectionDetailsRef.current) {
      // Use a small timeout to ensure the DOM has updated
      setTimeout(() => {
        sectionDetailsRef.current?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [sections]);

  // Function to sort data
  const sortData = (data: IUnitConfiguration[]): IUnitConfiguration[] => {
    if (!orderBy) {
      return data;
    }
    
    return [...data].sort((a, b) => {
      const aValue = a[orderBy as keyof IUnitConfiguration];
      const bValue = b[orderBy as keyof IUnitConfiguration];
      
      // Handle special cases like arrays
      if (orderBy === 'pheTypes') {
        const aString = Array.isArray(aValue) ? aValue.join(', ') : String(aValue || '');
        const bString = Array.isArray(bValue) ? bValue.join(', ') : String(bValue || '');
        return order === 'asc' 
          ? aString.localeCompare(bString)
          : bString.localeCompare(aString);
      }
      
      // Handle boolean values
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return order === 'asc' 
          ? (aValue === bValue ? 0 : aValue ? 1 : -1)
          : (aValue === bValue ? 0 : aValue ? -1 : 1);
      }
      
      // Handle string values
      if (aValue === null || aValue === undefined) return order === 'asc' ? -1 : 1;
      if (bValue === null || bValue === undefined) return order === 'asc' ? 1 : -1;
      
      const aString = String(aValue);
      const bString = String(bValue);
      
      return order === 'asc' 
        ? aString.localeCompare(bString)
        : bString.localeCompare(aString);
    });
  };
  
  // Handle sort request
  const handleRequestSort = (property: string) => {
    const column = dataColumns.find(col => col.id === property);
    if (!column || column.sortable === false) return;
    
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Box sx={styles.container}>
      <Stack spacing={3} sx={styles.contentWrapper }>
        <Box sx={{ 
          ...flexStyles.flexCenterBetween,
          mb: 4,
          mt: 1
        }}>
        </Box>
        
        <Box sx={styles.actionButtonsContainer}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('unitconfigurationmanagement/unitconfigurationeditor')}
            disabled={isLoading}
            sx={{ 
              whiteSpace: 'nowrap', 
              padding: '6px 26px !important',
              textTransform: 'none',
              boxShadow: 1
            }}
          >
            Create new configuration
          </Button>
          
          <FilterToggle 
            backPath=''
            isFiltering={isFiltering}
            showFilters={showFilters}
            isLoading={isLoading}
            toggleShowFilters={toggleShowFilters}
            onResetFilters={resetFilters}
            resultCount={filteredData?.length}
          />
        </Box>

        {showFilters && (
          <Paper 
            elevation={0} 
            sx={styles.filterContainer}
          >
            <UCManagementFilter 
              onChange={handleFilterChange}
              filter={unitConfigurationFilter}
              isFiltering={isFiltering}
              filteredData={filteredData}
              currentData={currentData}
            />
          </Paper>
        )}

        {isLoading ? (
          <Box sx={styles.loadingContainer}>
            <CircularProgress size={40} />
            <Typography variant="body1" ml={2}>
              Loading...
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3} alignItems="center" width="100%">
            {filteredData.length === 0 ? (
              <Paper 
                sx={{ 
                  p: 4, 
                  width: '100%', 
                  textAlign: 'center',
                  backgroundColor: theme.palette.grey[50],
                  border: '1px dashed',
                  borderColor: theme.palette.grey[300]
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  No unit configurations match the specified filters
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper} sx={styles.tableContainer}>
                <Table aria-label="unit configurations table" sx={styles.table}>
                  <TableHead>
                    <TableRow>
                      {dataColumns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.align || 'left'}
                          style={{ 
                            width: column.minWidth,
                            maxWidth: column.maxWidth,
                          }}
                          sx={styles.headerCell}
                          sortDirection={orderBy === column.id ? order : false}
                        >
                          {column.sortable !== false ? (
                            <TableSortLabel
                              active={orderBy === column.id}
                              direction={orderBy === column.id ? order : 'asc'}
                              onClick={() => handleRequestSort(column.id)}
                            >
                              {column.label}
                            </TableSortLabel>
                          ) : (
                            <Typography variant="body2" fontWeight="bold">
                              {column.label}
                            </Typography>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortData(filteredData).map((row, index) => {
                      const rowKey = getUnitConfigKey(row);
                      return (
                        <MemoizedTableRow
                          key={rowKey + "-" + index}
                          row={row}
                          columns={dataColumns}
                          isSelected={selectedRowKey === rowKey}
                          onRowClick={handleRowClick}
                          styles={styles}
                          theme={theme}
                        />
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {sections && sections.length > 0 && (
              <Paper 
                elevation={1} 
                sx={{ p: 3, width: '100%' }}
                ref={sectionDetailsRef}
              >
                <Typography variant="h6" mb={2}>Section Details</Typography>
                <MultiSectionTiles sections={sections} />
              </Paper>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}; 